import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronUp, ChevronDown, Close } from '../../shared/components/Icons';
import { InfoTooltip } from '../../shared/components/InfoTooltip';
import { formatDate } from '../../shared/utils/dateUtils';
import { api, trackEvent } from '../../shared/utils/api';
import { confColor, Badge, _thRed, _thGreen } from './confColorUtils';
import { DiagnosticZone, DEFAULT_STATUSES, getStatusColor, SRALinks } from './TopicDetail';
import QuizPanel from './QuizPanel';
import TopicQuestions from './TopicQuestions';
import ROUTES from '../../shared/constants/routes';

function TopicRow({ topic, onSave, forceOpen, moduleColor, isFocused, statuses, tourHighlight, sraStat, onNavigateToSRA }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [conf, setConf] = useState(topic.confidence!==null?Math.round(topic.confidence*100):50);
  const [hasConf, setHasConf] = useState(topic.confidence!==null);
  const [status, setStatus] = useState(topic.status||'');
  const [notes, setNotes] = useState(topic.notes||'');
  const [notesFocused, setNotesFocused] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [diagnostic, setDiagnostic] = useState(null);
  const [questionStatus, setQuestionStatus] = useState(null);
  const [quizOpen, setQuizOpen] = useState(false);
  const diagCache = useRef(null);
  const qStatusCache = useRef(null);
  const clearTimer = useRef(null);
  const saveTimer = useRef(null);
  const rowRef = useRef(null);

  useEffect(()=>{
    if(isFocused){
      setOpen(true);
      setTimeout(()=>{
        if(rowRef.current){
          const top = rowRef.current.getBoundingClientRect().top + window.pageYOffset - 80;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }, 100);
    }
  },[isFocused]);
  useEffect(()=>{ if(forceOpen!==null) setOpen(forceOpen); },[forceOpen]);

  // Lazy-load diagnostic + question status when expanded (both cached)
  useEffect(()=>{
    if (open && !diagCache.current) {
      api(`/api/topics/${topic.id}/diagnostic`).then(d => {
        diagCache.current = d;
        setDiagnostic(d);
      }).catch(() => setDiagnostic({}));
    } else if (open && diagCache.current) {
      setDiagnostic(diagCache.current);
    }
    if (open && !qStatusCache.current) {
      api(`/api/questions/topic/${topic.id}/status`).then(d => {
        qStatusCache.current = d;
        setQuestionStatus(d);
      }).catch(() => {});
    } else if (open && qStatusCache.current) {
      setQuestionStatus(qStatusCache.current);
    }
  }, [open, topic.id]);

  const save = useCallback(async(confVal,hasC,statusVal,notesVal)=>{
    setSaving(true);
    const confNum = hasC?Math.min(100,Math.max(0,parseInt(confVal)))/100:null;
    const wasUnset = !topic.confidence && topic.confidence !== 0;
    await api(`/api/topics/${topic.id}`,{method:'PATCH',body:JSON.stringify({
      confidence:(isNaN(confNum)||confNum===null)?null:confNum,
      status:statusVal,notes:notesVal,
      date_updated:new Date().toLocaleDateString('en-GB',{day:'2-digit',month:'2-digit',year:'numeric'}).replace(/\//g,'.'),
      is_estimated: false,
    })});
    if (hasC && wasUnset) trackEvent('first_confidence_set');
    setSaving(false); setSaved(true);
    setTimeout(()=>setSaved(false),1500);
    onSave&&onSave(topic.id,{confidence:(isNaN(confNum)||confNum===null)?null:confNum,status:statusVal,notes:notesVal,is_estimated:false});
  },[topic.id,onSave]);

  const autoSave=(cVal,hasC,sVal,nVal)=>{
    clearTimeout(saveTimer.current);
    saveTimer.current=setTimeout(()=>save(cVal,hasC,sVal,nVal),600);
  };

  const startClearConfidence=()=>{
    setConfirmClear(true);
    clearTimeout(clearTimer.current);
    clearTimer.current=setTimeout(()=>setConfirmClear(false),3000);
  };
  const confirmClearConfidence=async()=>{
    clearTimeout(clearTimer.current);
    setConfirmClear(false);
    await api(`/api/topics/${topic.id}/clear-confidence`,{method:'POST'});
    setHasConf(false); setConf(50);
    onSave&&onSave(topic.id,{confidence:null});
  };
  const cancelClear=()=>{
    clearTimeout(clearTimer.current);
    setConfirmClear(false);
  };

  const handleOpenQuiz = (e) => {
    e.stopPropagation();
    setQuizOpen(true);
  };

  const handleCloseQuiz = () => {
    setQuizOpen(false);
    // Refresh question status
    api(`/api/questions/topic/${topic.id}/status`).then(d => {
      qStatusCache.current = d;
      setQuestionStatus(d);
    }).catch(() => {});
  };

  const handleAllAnswered = async () => {
    const data = await api(`/api/topics/${topic.module}`);
    const updated = Array.isArray(data) ? data.find(t => t.id === topic.id) : null;
    if (updated) onSave(topic.id, { confidence: updated.confidence });
    return updated?.confidence ?? null;
  };

  const focusRing=isFocused?{boxShadow:`0 0 0 2px ${moduleColor}`,borderColor:moduleColor}:{};
  // Use sub_subtopic (new) or detail (legacy) for the detail text
  const detailText = topic.sub_subtopic || topic.detail || '';

  return (
    <div ref={rowRef} className={`topic-row ${open?'open':''} ${topic.is_estimated?'is-estimated':''}`} style={focusRing}>
      <div className="topic-header" onClick={()=>setOpen(o=>!o)}>
        <div className="topic-info">
          <div className="topic-name">
            {topic.display_name || topic.subtopic}
            {topic.is_estimated && <span className="estimated-badge" title="Estimated from subject-area rating">est.<InfoTooltip text="This confidence was estimated from your MCQ score on a broader topic area. Rate it yourself for a more accurate picture." /></span>}
          </div>
          {detailText && !open && (
            <div className="topic-meta topic-detail-preview">{detailText}</div>
          )}
        </div>
        <div className="topic-right">
          {questionStatus && questionStatus.available > 0 && !quizOpen && (
            <button className="free-q-btn" onClick={handleOpenQuiz}>
              {questionStatus.available} question{questionStatus.available !== 1 ? 's' : ''} left
            </button>
          )}
          {questionStatus && questionStatus.available === 0 && questionStatus.completed > 0 && (
            <span className="q-done-label">{questionStatus.score} correct</span>
          )}
          <Badge c={topic.confidence} status={topic.status}/>
          <div className="conf-wrap">
            <div className="conf-bar-bg"><div className="conf-bar-fill" style={{width:(topic.confidence!==null?Math.round(topic.confidence*100):0)+'%',background:confColor(topic.confidence)}}/></div>
            <span className="conf-pct" style={{color:confColor(topic.confidence)}} aria-label={topic.confidence!==null?`Confidence: ${Math.round(topic.confidence*100)} percent, ${topic.confidence < _thRed ? 'needs work' : topic.confidence < _thGreen ? 'moderate' : 'strong'}`:undefined}>{topic.confidence!==null?Math.round(topic.confidence*100)+'%':'—'}</span>
          </div>
          <span className="chevron">{open?<ChevronUp size={10} />:<ChevronDown size={10} />}</span>
        </div>
      </div>

      {quizOpen && (
        <QuizPanel
          topicId={topic.id}
          oldConfidence={topic.confidence}
          onClose={handleCloseQuiz}
          onAllAnswered={handleAllAnswered}
        />
      )}

      {open&&(
        <div className="topic-expand" onClick={e=>e.stopPropagation()}>
          {detailText&&<div className="topic-detail">{detailText}</div>}
          {topic.principle_count > 0 && (
            <div className="topic-principle-count">{topic.principle_count} principles</div>
          )}

          {/* ── Diagnostic zone ── */}
          <DiagnosticZone diagnostic={diagnostic} topic={topic} onNavigateToSRA={onNavigateToSRA} />

          {topic.id && (
            <TopicQuestions
              topicId={topic.id}
              mcqStats={diagnostic?.mcq_stats || null}
              module={topic.module}
            />
          )}

          {/* ── Input zone ── */}
          <div className="expand-input-zone">
            {/* Status badge (auto-derived) + optional override dropdown */}
            <div className="status-conf-row">
              <div className="field-group field-group-status">
                <label className="field-label">Status</label>
                <select className="field-select" value={status}
                  style={status ? { background: (getStatusColor(status, statuses) || '#F3F4F6') + '80' } : {}}
                  onChange={e=>{setStatus(e.target.value);autoSave(conf,hasConf,e.target.value,notes);}}>
                  <option value="">— auto —</option>
                  {(statuses||DEFAULT_STATUSES).map(s=><option key={typeof s==='string'?s:s.label} value={typeof s==='string'?s:s.label}>{typeof s==='string'?s:s.label}</option>)}
                </select>
              </div>

              {/* Confidence slider */}
              <div className="field-group field-group-conf">
                <label className="field-label" style={{display:'flex',alignItems:'center',gap:8}}>
                  Confidence
                  {hasConf&&<span style={{color:confColor(conf/100),fontFamily:'var(--mono)',fontSize:13}}>{conf}%</span>}
                </label>
                {!hasConf?(
                  <button className="set-conf-btn" onClick={()=>{setHasConf(true);autoSave(50,true,status,notes);}}>Set confidence</button>
                ):(
                  <div className={`slider-wrap${tourHighlight ? ' tour-highlight' : ''}`}>
                    <input type="range" min="0" max="100" step="5" value={conf} className="conf-slider"
                      style={{'--slider-color':confColor(conf/100), background:`linear-gradient(to right, ${confColor(conf/100)} ${conf}%, var(--border) ${conf}%)`}}
                      onChange={e=>{setConf(parseInt(e.target.value));autoSave(e.target.value,true,status,notes);}}/>
                    <div className="slider-labels"><span>0</span><span>50</span><span>100</span></div>
                    <div className="clear-conf-row">
                      {!confirmClear&&<button className="clear-conf-btn" onClick={startClearConfidence}><Close size={10} /> Clear rating</button>}
                      {confirmClear&&<span className="clear-confirm-inline"><span>Clear this rating?</span><button className="clear-confirm-yes" onClick={confirmClearConfidence}>Yes</button><button className="clear-confirm-no" onClick={cancelClear}>No</button></span>}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Readiness display — decayed confidence, read-only */}
            {(topic.decayed_confidence !== null && topic.decayed_confidence !== undefined) && (
              <div className="readiness-row">
                <span className="field-label">
                  Readiness
                  <InfoTooltip text="Calculated from your confidence rating, MCQ accuracy, diagnostic scores, and how recently you reviewed this topic." />
                </span>
                <div>
                  {topic.is_insufficient_data ? (
                    <span className="readiness-value readiness-no-data">
                      no score yet
                    </span>
                  ) : (
                    <span className="readiness-value" style={{ color: confColor(topic.decayed_confidence) }}>
                      {Math.round(topic.decayed_confidence * 100)}%
                    </span>
                  )}
                  {topic.review_count > 0 && (
                    <div className="topic-review-count">{topic.review_count} review{topic.review_count !== 1 ? 's' : ''}</div>
                  )}
                </div>
              </div>
            )}

            {/* Notes — single-line input that expands on focus */}
            <div className="field-group" style={{marginTop:8}}>
              {(notesFocused || notes) ? (
                <textarea className="field-notes" placeholder="e.g. Weak on s.423 IA 1986, revisit. MCQs drilled ×2."
                  value={notes} autoFocus={notesFocused && !notes}
                  onFocus={() => setNotesFocused(true)}
                  onBlur={() => { if (!notes) setNotesFocused(false); }}
                  onChange={e=>{setNotes(e.target.value);autoSave(conf,hasConf,status,e.target.value);}}/>
              ) : (
                <input className="field-notes-collapsed" type="text" readOnly
                  placeholder="Add a note"
                  onFocus={() => setNotesFocused(true)}
                  onClick={() => setNotesFocused(true)} />
              )}
            </div>

            {/* SRA Links for Solicitors Accounts topics */}
            {topic.topic && topic.topic.toLowerCase().includes('solicitor') && (
              <SRALinks />
            )}
          </div>

          <div className="save-status">
            {saving&&<span className="save-saving">Saving</span>}
            {saved&&!saving&&<span className="save-done">Saved</span>}
            {!saving&&!saved&&topic.date_updated&&<span className="save-date">Updated {formatDate(topic.date_updated, 'relative')}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

export default TopicRow;
